import { ComponentType, ReactNode } from 'react';

import { ColumnDef, SortingState } from '@tanstack/react-table';

import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { InboundMetricDrillKey } from 'stores/inboundReferralsInsightsStore';

import {
  buildAcceptedColumns,
  buildAcceptedRows,
  buildDeniedColumns,
  buildDeniedRowsWithSubRows,
  buildSelectedColumns,
  buildSelectedRows,
} from '../table/inboundReferralDetailsColumns';

import AcceptedDetailHeader from './AcceptedDetailHeader';
import DeniedDetailHeader from './DeniedDetailHeader';
import SelectedDetailHeader from './SelectedDetailHeader';

// Every metric-drill header takes the same props; each uses what it needs. `loading` is switch-aware
// (true during a View By switch) for elements that change per View By (the per-entity chart);
// `initialLoading` is first-open only, for the tiles/breakdowns whose value is the same across View By.
export type InboundMetricHeaderProps = {
  loading: boolean;
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

type BuildTableArgs = {
  data: AnalyticsResponseData;
  rollups: AnalyticsResponseRollupCollection;
  entityHeader: string;
};

// A drill's breakdown-table shape. Rows/columns are drill-specific (Accepted is flat, Denied
// expands into reason sub-rows), so each drill supplies its own builders rather than toggling
// columns over one shared schema.
export type InboundDrillTable = {
  columns: ColumnDef<any, any>[];
  rows: any[];
  defaultSortBy: SortingState;
  getSubRows?: (row: any) => any[] | undefined;
  expandable: boolean;
};

type InboundMetricDrill = {
  Header: ComponentType<InboundMetricHeaderProps>;
  buildTable: (args: BuildTableArgs) => InboundDrillTable;
};

// Central registry of the built metric drills. Adding a drill = one entry here (header + table
// builder) plus its metric set in constants — the orchestrator and breakdown table dispatch off
// this map instead of per-metric branches. Titles/export slugs stay in constants (they cover the
// full key space, including drills not built yet).
export const INBOUND_METRIC_DRILLS: Partial<Record<InboundMetricDrillKey, InboundMetricDrill>> = {
  accepted: {
    Header: AcceptedDetailHeader,
    buildTable: ({ data, entityHeader }) => ({
      columns: buildAcceptedColumns(entityHeader),
      rows: buildAcceptedRows(data),
      // Default sort is the drill's rate (acceptance rate), matching the chart order.
      defaultSortBy: [{ id: 'acceptanceRate', desc: true }],
      expandable: false,
    }),
  },
  denied: {
    Header: DeniedDetailHeader,
    buildTable: ({ data, rollups, entityHeader }) => ({
      columns: buildDeniedColumns(entityHeader),
      rows: buildDeniedRowsWithSubRows(data, rollups),
      // Default sort is the drill's rate (denial rate); reason sub-rows sort within their parent.
      defaultSortBy: [{ id: 'denialRate', desc: true }],
      getSubRows: (row) => row.subRows,
      expandable: true,
    }),
  },
  selected: {
    Header: SelectedDetailHeader,
    buildTable: ({ data, entityHeader }) => ({
      columns: buildSelectedColumns(entityHeader),
      rows: buildSelectedRows(data),
      // Default sort is the drill's rate (selected rate), matching the chart order.
      defaultSortBy: [{ id: 'selectedRate', desc: true }],
      expandable: false,
    }),
  },
};

export function renderInboundMetricHeader(metric: InboundMetricDrillKey, props: InboundMetricHeaderProps): ReactNode {
  const Header = INBOUND_METRIC_DRILLS[metric]?.Header;
  return Header ? <Header {...props} /> : null;
}
